import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Hello: React.FC = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [visibleDots, setVisibleDots] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [helloText, setHelloText] = useState("");
  const [isTypingHello, setIsTypingHello] = useState(false);
  const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentIndexRef = useRef<number>(0);
  const helloIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const helloIndexRef = useRef<number>(0);

  const totalDots = 10;
  const typingText = "thats makes our experience";
  const helloTitle = "Experience - Feelings Causes";

  // Generate random colors for each dot - variations of anxiety (red), apathy (light blue), flow (yellow), comfort (green)
  const dotColors = useMemo(() => {
    const colors = [
      // Red variations (anxiety)
      "#dc3545",
      "#c82333",
      "#e74c3c",
      "#ff4757",
      "#ff6b6b",
      "#ee5a6f",
      // Light blue variations (apathy)
      "#87ceeb",
      "#b0e0e6",
      "#add8e6",
      "#a8d8f0",
      "#b8e0f2",
      "#c8e8f5",
      // Yellow variations (flow)
      "#ffc107",
      "#ffd700",
      "#ffeb3b",
      "#fff176",
      "#ffd54f",
      "#ffca28",
      // Green variations (comfort)
      "#28a745",
      "#34ce57",
      "#4caf50",
      "#66bb6a",
      "#81c784",
      "#a5d6a7",
    ];
    return Array.from(
      { length: totalDots },
      () => colors[Math.floor(Math.random() * colors.length)]
    );
  }, []);

  // Generate random positions within a larger square area
  const dotPositions = useMemo(() => {
    const squareSize = 300; // Increased from 120 to 300 for more space
    return Array.from({ length: totalDots }, () => ({
      top: Math.random() * squareSize,
      left: Math.random() * squareSize,
    }));
  }, []);

  // Generate random connections (30% of all possible connections, ensuring all dots are connected)
  const connections = useMemo(() => {
    // Generate all possible connections
    const allConnections: Array<[number, number]> = [];
    for (let i = 0; i < totalDots; i++) {
      for (let j = i + 1; j < totalDots; j++) {
        allConnections.push([i, j]);
      }
    }

    const selectedConnections: Array<[number, number]> = [];
    const connectionSet = new Set<string>();

    // Step 1: Create a spanning tree to ensure all dots are connected
    // This guarantees that every dot can be reached from any other dot
    const connectedNodes = new Set<number>([0]); // Start with node 0

    while (connectedNodes.size < totalDots) {
      // Find a node not yet in the connected set
      const unconnectedNodes = Array.from(
        { length: totalDots },
        (_, i) => i
      ).filter((i) => !connectedNodes.has(i));

      if (unconnectedNodes.length === 0) break;

      // Pick a random unconnected node
      const newNode =
        unconnectedNodes[Math.floor(Math.random() * unconnectedNodes.length)];

      // Connect it to a random already-connected node
      const connectedArray = Array.from(connectedNodes);
      const existingNode =
        connectedArray[Math.floor(Math.random() * connectedArray.length)];

      // Add the connection (ensure smaller index first for consistency)
      const connection: [number, number] =
        existingNode < newNode
          ? [existingNode, newNode]
          : [newNode, existingNode];

      const connectionKey = `${connection[0]}-${connection[1]}`;
      if (!connectionSet.has(connectionKey)) {
        connectionSet.add(connectionKey);
        selectedConnections.push(connection);
      }

      connectedNodes.add(newNode);
    }

    // Step 2: Add additional random connections up to 30% of total possible
    const targetConnectionCount = Math.floor(allConnections.length * 0.3);
    const remainingConnections = allConnections.filter((conn) => {
      const key = `${conn[0]}-${conn[1]}`;
      return !connectionSet.has(key);
    });

    // Shuffle remaining connections
    const shuffled = [...remainingConnections].sort(() => Math.random() - 0.5);

    // Add connections until we reach 30% or run out
    for (const conn of shuffled) {
      if (selectedConnections.length >= targetConnectionCount) break;
      const key = `${conn[0]}-${conn[1]}`;
      if (!connectionSet.has(key)) {
        connectionSet.add(key);
        selectedConnections.push(conn);
      }
    }

    return selectedConnections;
  }, [totalDots]);

  // Typing animation for "Hello" title at the start
  useEffect(() => {
    setIsTypingHello(true);
    setHelloText("");
    helloIndexRef.current = 0;

    helloIntervalRef.current = setInterval(() => {
      if (helloIndexRef.current < helloTitle.length) {
        setHelloText(helloTitle.substring(0, helloIndexRef.current + 1));
        helloIndexRef.current++;
      } else {
        if (helloIntervalRef.current) {
          clearInterval(helloIntervalRef.current);
          helloIntervalRef.current = null;
        }
      }
    }, 150); // Type each character every 150ms

    return () => {
      if (helloIntervalRef.current) {
        clearInterval(helloIntervalRef.current);
        helloIntervalRef.current = null;
      }
    };
  }, []);

  // Animate dots: 0 -> 1 -> 2 -> ... -> 10 -> stop (don't reset)
  // Start dots animation after Hello is typed
  // useEffect(() => {
  //     if (!isTypingHello || helloText.length < helloTitle.length) {
  //         return; // Wait for Hello to finish typing
  //     }

  //     if (visibleDots >= totalDots) {
  //         return; // Stop animation when all dots are visible
  //     }

  //     let interval: NodeJS.Timeout | null = null;

  //     // Small delay before starting dots
  //     const delay = setTimeout(() => {
  //         interval = setInterval(() => {
  //             setVisibleDots((prev) => {
  //                 if (prev >= totalDots) {
  //                     return prev; // Don't increment beyond totalDots
  //                 }
  //                 return prev + 1;
  //             });
  //         }, 300); // Slightly faster to accommodate more dots
  //     }, 500);

  //     return () => {
  //         clearTimeout(delay);
  //         if (interval) {
  //             clearInterval(interval);
  //         }
  //     };
  // }, [visibleDots, totalDots, isTypingHello, helloText, helloTitle]);

  // Typing animation after all dots are visible
  // useEffect(() => {
  //     if (visibleDots === totalDots && !isTyping) {
  //         // Wait a bit before starting typing animation
  //         const delay = setTimeout(() => {
  //             setIsTyping(true);
  //             setDisplayedText('');
  //             currentIndexRef.current = 0;

  //             typingIntervalRef.current = setInterval(() => {
  //                 if (currentIndexRef.current < typingText.length) {
  //                     setDisplayedText(typingText.substring(0, currentIndexRef.current + 1));
  //                     currentIndexRef.current++;
  //                 } else {
  //                     if (typingIntervalRef.current) {
  //                         clearInterval(typingIntervalRef.current);
  //                         typingIntervalRef.current = null;
  //                     }
  //                 }
  //             }, 100); // Type each character every 100ms
  //         }, 500); // Wait 500ms after all dots are visible

  //         return () => {
  //             clearTimeout(delay);
  //             if (typingIntervalRef.current) {
  //                 clearInterval(typingIntervalRef.current);
  //                 typingIntervalRef.current = null;
  //             }
  //         };
  //     }
  // }, [visibleDots, totalDots, isTyping, typingText]);

  return (
    <>
      <style>{`
                @media (max-width: 768px) {
                    .hello-title {
                        font-size: clamp(28px, 8vw, 48px) !important;
                    }
                    .hello-title-cursor {
                        height: clamp(28px, 8vw, 48px) !important;
                    }
                    .hello-subtitle {
                        font-size: clamp(16px, 4vw, 18px) !important;
                        margin-bottom: clamp(24px, 6vw, 48px) !important;
                    }
                    .hello-about-text {
                        font-size: clamp(16px, 4vw, 18px) !important;
                    }
                    .hello-about-padding {
                        padding: clamp(16px, 4vw, 24px) !important;
                    }
                    .hello-page-padding {
                        padding: clamp(20px, 5vw, 60px) clamp(16px, 4vw, 20px) 0 clamp(16px, 4vw, 20px) !important;
                    }
                    .hello-email-text {
                        font-size: clamp(16px, 4vw, 18px) !important;
                    }
                    .hello-email-link {
                        font-size: clamp(18px, 5vw, 20px) !important;
                    }
                    .hello-link {
                        font-size: clamp(16px, 4vw, 18px) !important;
                    }
                    .hello-image {
                        max-width: min(70vw, 300px) !important;
                    }
                    .hello-mood-image {
                        max-width: min(40vw, 150px) !important;
                    }
                    .hello-mood-image-third {
                        display: none !important;
                    }
                    .hello-mood-images-container {
                        margin-bottom: clamp(32px, 8vw, 48px) !important;
                    }
                }
            `}</style>
      <div
        style={{
          backgroundColor: "#ffffff",
          color: "#333",
        }}
      >
        {/* First Page - Full Viewport Height */}
        <div
          style={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            backgroundColor: "#ffffff",
          }}
        >
          <div
            style={{
              textAlign: "center",
              maxWidth: "600px",
              width: "100%",
            }}
          >
            <h1
              className="hello-title"
              style={{
                fontSize: "clamp(28px, 8vw, 48px)",
                fontWeight: "300",
                margin: "0 0 16px 0",
                color: "#2c3e50",
                letterSpacing: "-1px",
                minHeight: "clamp(40px, 10vw, 60px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {helloText || "\u00A0"}
              {helloText.length < helloTitle.length && (
                <span
                  className="hello-title-cursor"
                  style={{
                    display: "inline-block",
                    width: "3px",
                    height: "clamp(28px, 8vw, 48px)",
                    backgroundColor: "#2c3e50",
                    marginLeft: "4px",
                    animation: "blink 1s infinite",
                    verticalAlign: "middle",
                  }}
                />
              )}
            </h1>
            <p
              className="hello-subtitle"
              style={{
                fontSize: "clamp(16px, 4vw, 18px)",
                color: "#666",
                margin: "0 0 clamp(24px, 6vw, 48px) 0",
                fontWeight: "300",
                lineHeight: "1.6",
              }}
            ></p>

            {/* Mood Images */}
            <div
              className="hello-mood-images-container"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "16px",
                flexWrap: "wrap",
                marginBottom: "48px",
              }}
            >
              <img
                src="/colorGirl/flow123.png"
                alt="Flow"
                className="hello-mood-image"
                style={{
                  maxWidth: "150px",
                  width: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                }}
              />
              <img
                src="/colorGirl/anxiouss.png"
                alt="Anxiety"
                className="hello-mood-image"
                style={{
                  maxWidth: "150px",
                  width: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                }}
              />
              <img
                src="/colorGirl/apathicg.png"
                alt="Apathy"
                className="hello-mood-image hello-mood-image-third"
                style={{
                  maxWidth: "150px",
                  width: "100%",
                  height: "auto",
                  borderRadius: "8px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                }}
              />
            </div>
          </div>
        </div>

        {/* Second Page - About Section Only */}
        <div
          className="hello-page-padding"
          style={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px 20px 0 20px",
            backgroundColor: "#ffffff",
          }}
        >
          <div
            style={{
              textAlign: "center",
              maxWidth: "900px",
              width: "100%",
            }}
          >
            {/* About Section */}
            <div
              className="hello-about-padding"
              style={{
                textAlign: "left",
                padding: "24px",
                paddingBottom: "24px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
              }}
            >
              <p
                className="hello-about-text"
                style={{
                  fontSize: "clamp(16px, 4vw, 18px)",
                  color: "#666",
                  margin: "0 0 12px 0",
                  lineHeight: "1.6",
                }}
              >
                Experience is a feeling tracking application that supports you
                in tracking and analyzing your experience patterns, monitoring
                flow state experiences, and collaborating with your team.
              </p>
              <p
                className="hello-about-text"
                style={{
                  fontSize: "clamp(16px, 4vw, 18px)",
                  color: "#666",
                  margin: "0 0 12px 0",
                  lineHeight: "1.6",
                }}
              >
                This app was inspired by <strong>flow psychology</strong>, a
                concept developed by <strong>Mihaly Csikszentmihalyi</strong>.
                Flow is the mental state of complete absorption in an activity,
                where you feel fully focused and energized.
              </p>
              <p
                className="hello-about-text"
                style={{
                  fontSize: "clamp(16px, 4vw, 18px)",
                  color: "#666",
                  margin: "0 0 16px 0",
                  lineHeight: "1.6",
                }}
              >
                Experience grew out of an idea discovered by{" "}
                <strong>Ruslan</strong> — a software engineer, professional
                fencer, and the project's leader. While competing in team
                events, it became evident that the mental state of each person
                influenced everyone else, revealing the powerful dynamics of
                collective focus and flow.
              </p>
              {/* Ruslan Photo */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <img
                  src="/ruslan.jpg"
                  alt="Ruslan - Professional Fencer"
                  className="hello-image"
                  style={{
                    maxWidth: "400px",
                    width: "100%",
                    height: "auto",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  }}
                />
              </div>

              {/* Support Email */}
              <div
                style={{
                  textAlign: "center",
                  marginTop: "24px",
                  paddingTop: "24px",
                  borderTop: "1px solid #dee2e6",
                }}
              >
                <p
                  className="hello-email-text"
                  style={{
                    fontSize: "clamp(16px, 4vw, 18px)",
                    color: "#666",
                    margin: "0 0 12px 0",
                    lineHeight: "1.6",
                  }}
                >
                  For support, questions, or feedback:
                </p>
                <a
                  href="mailto:support@experienceapp.io"
                  className="hello-email-link"
                  style={{
                    color: "#007bff",
                    textDecoration: "none",
                    fontSize: "clamp(18px, 5vw, 20px)",
                    fontWeight: "400",
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                  }}
                >
                  support@experienceapp.io
                </a>
              </div>

              {/* Terms and Privacy Links */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  alignItems: "center",
                  marginTop: "24px",
                  paddingTop: "24px",
                  paddingBottom: "0",
                  borderTop: "1px solid #dee2e6",
                }}
              >
                <Link
                  to="/terms"
                  className="hello-link"
                  style={{
                    color: "#007bff",
                    textDecoration: "none",
                    fontSize: "clamp(16px, 4vw, 18px)",
                    fontWeight: "400",
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                  }}
                >
                  Terms of Service
                </Link>
                <Link
                  to="/privacy"
                  className="hello-link"
                  style={{
                    color: "#007bff",
                    textDecoration: "none",
                    fontSize: "clamp(16px, 4vw, 18px)",
                    fontWeight: "400",
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.7";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                  }}
                >
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Hello;
